import type {ReactNode} from 'react';
import {createPortal} from 'react-dom';

import {isNil} from 'ramda';

import type {TruncatedCellHoverState} from '../../hooks/useTruncatedCellHover';

/** Keep the overlay from touching the viewport's right edge. */
const VIEWPORT_EDGE_GAP_PX = 8;

/** One palette step lighter than the keyboard-focus accent so the highlight reads
 * softer. Inline style instead of a Tailwind arbitrary-value class — the generated
 * class was not reliably emitted. */
const HIGHLIGHT_BORDER = '2px solid var(--color-palettes-blue-50-100)';

export type ExpandedCellOverlayProps = {
  hoverState: TruncatedCellHoverState | null;
  /** What the expanded cell shows — plain text, or rich content (e.g. flag badges). */
  content: ReactNode;
  /** Row-height padding class of the cell so the expanded text stays visually aligned with it. */
  paddingClass: string;
};

/**
 * Sheets-style "expanded cell" reveal for truncated content: an overlay that
 * looks like the cell itself — exact row height, flat white background, thin
 * border — with the full text on a single line spilling over the neighbouring
 * cells (clamped only at the viewport edge). Rendered in a portal because the
 * cell clips its content (overflow-hidden), and with pointer-events disabled
 * so the pointer keeps hitting the cell underneath — otherwise entering the
 * overlay would fire the cell's mouseleave and the overlay would flicker.
 */
export function ExpandedCellOverlay(props: ExpandedCellOverlayProps) {
  if (isNil(props.hoverState)) {
    return null;
  }

  const anchorRect = props.hoverState.anchorRect;
  const maxWidth = window.innerWidth - anchorRect.left - VIEWPORT_EDGE_GAP_PX;

  return createPortal(
    <div
      className={`fixed z-(--z-tooltip) pointer-events-none flex items-center overflow-hidden bg-white shadow-sm text-[13px] text-[#091E42] whitespace-nowrap ${props.paddingClass}`}
      style={{
        top: anchorRect.top,
        left: anchorRect.left,
        // Clamped so min-width cannot beat max-width and push past the viewport gap
        // when the cell's right edge is already within the gap.
        minWidth: Math.min(anchorRect.width, maxWidth),
        maxWidth,
        height: anchorRect.height,
        border: HIGHLIGHT_BORDER,
      }}
      data-testid="expanded-cell-overlay"
    >
      {props.content}
    </div>,
    document.body
  );
}
